import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../hooks/useAuth';
import { apiService } from '../services/api';
import { Competition } from '../types/interfaces';
import Button from '../components/common/Button';
import LoadingSpinner from '../components/common/LoadingSpinner';
import CompetitionTypePill from '../components/common/CompetitionTypePill';
import CompetitionModePill from '../components/common/CompetitionModePill';
import CompetitionVisibilityPill from '../components/common/CompetitionVisibilityPill';
import Leaderboard from '../components/common/Leaderboard';
import SiteFooter from '../components/common/SiteFooter';
import { formatDate } from '../utils/dates';
import './Competition.css';
import './PublicCompetition.css';
import TourButton from '../components/tour/TourButton';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const Timeline: React.FC<{ startDate: string; endDate: string }> = ({ startDate, endDate }) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const today = new Date();

  const totalDuration = end.getTime() - start.getTime();
  const elapsed = today.getTime() - start.getTime();
  const progressPercent = Math.min(Math.max((elapsed / totalDuration) * 100, 0), 100);
  const [hovered, setHovered] = useState(false);

  return (
    <div className="timeline-container">
      <div className="timeline-labels">
        <span className="timeline-date">{formatDate(start)}</span>
        <span className="timeline-date today">Today: {formatDate(today)}</span>
        <span className="timeline-date">{formatDate(end)}</span>
      </div>
      <div
        className="timeline-bar"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div className="timeline-progress" style={{ width: `${progressPercent}%` }}>
          <div className="timeline-today-indicator">Today</div>
        </div>
        {hovered && (
          <div className="timeline-tooltip" style={{ left: `${progressPercent}%` }}>
            {`Progress: ${progressPercent.toFixed(1)}%`}
          </div>
        )}
      </div>
    </div>
  );
};

const PublicCompetition: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated } = useAuth();
  const [competition, setCompetition] = useState<Competition | null>(null);
  useDocumentTitle(competition?.title);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [markdownContent, setMarkdownContent] = useState<string>('');
  const [markdownLoading, setMarkdownLoading] = useState(false);
  const [markdownError, setMarkdownError] = useState('');
  const [isMarkdownExpanded, setIsMarkdownExpanded] = useState(false);

  const isTeamMode = (competition?.competition_mode || 'individual') === 'team';
  const statusClass = (competition?.status || 'default').toLowerCase().replace(/\s+/g, '-');
  const normalizedPrize = (competition?.prize || '').toLowerCase();
  const prizeTagLabel =
    normalizedPrize === 'money'
      ? 'Prize: Money prizes'
      : normalizedPrize === 'badge'
        ? 'Prize: Certificate'
        : null;

  const participantUnit = useMemo(() => {
    if (!competition?.participants && competition?.participants !== 0) return '';
    const singular = isTeamMode ? 'team' : 'participant';
    const plural = isTeamMode ? 'teams' : 'participants';
    return competition.participants === 1 ? singular : plural;
  }, [competition?.participants, isTeamMode]);

  useEffect(() => {
    if (!id) return;
    const fetchCompetition = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await apiService.get(`/competitions/public/${id}`);
        setCompetition(response.data);
      } catch (err: any) {
        setError(err?.message || 'Competition not found');
      } finally {
        setLoading(false);
      }
    };
    fetchCompetition();
  }, [id]);

  useEffect(() => {
    if (!competition?.instructions_path) {
      setMarkdownContent('');
      return;
    }
    const fetchMarkdown = async () => {
      try {
        setMarkdownLoading(true);
        setMarkdownError('');
        const response = await fetch(competition.instructions_path!);
        if (!response.ok) {
          throw new Error('Failed to fetch instructions');
        }
        const text = await response.text();
        setMarkdownContent(text);
      } catch (err) {
        setMarkdownError('Failed to load instructions');
        console.error('Error loading markdown:', err);
      } finally {
        setMarkdownLoading(false);
      }
    };
    fetchMarkdown();
  }, [competition?.instructions_path]);

  if (loading) {
    return <LoadingSpinner text="Loading public competition..." />;
  }

  if (error || !competition) {
    return (
      <div className="public-competition-shell">
        <div className="public-competition-error-card" data-tour="error-card">
          <h1>Competition not available</h1>
          <p>{error || 'This competition is not available for public viewing.'}</p>
          <div className="public-competition-error-actions">
            <Button as={Link} to="/" variant="primary" size="md">Home</Button>
            <Button as={Link} to={isAuthenticated ? '/dashboard' : '/login'} variant="outline" size="md">
              {isAuthenticated ? 'Dashboard' : 'Sign in'}
            </Button>
          </div>
        </div>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="public-competition-shell">
      <section className="public-competition-nav" data-tour="public-nav">
        <div className="container public-competition-nav__inner">
          <p className="public-competition-nav__eyebrow">iKompete Public Showcase</p>
          <div className="public-competition-nav__actions">
            <TourButton />
            <Button as={Link} to="/" variant="outline" size="sm">Home</Button>
            <Button as={Link} to={isAuthenticated ? '/dashboard' : '/login'} variant="secondary" size="sm">
              {isAuthenticated ? 'Dashboard' : 'Login'}
            </Button>
          </div>
        </div>
      </section>

      <div className="competition-page competition-page--public">
        <div className="competition-hero" data-tour="public-hero">
          <div className="competition-hero__left">
            <h1>{competition.title}</h1>
            {competition.logo_path && (
              <img
                src={competition.logo_path}
                alt={`${competition.title} logo`}
                className="competition-logo"
              />
            )}
          </div>

          <div className="competition-hero__cta">
            <div className="competition-access-pill competition-access-pill--archived">Archived</div>
          </div>
        </div>

        <section className="competition-meta-grid" data-tour="public-meta">
          <p className="competition-meta-row competition-meta-row--full">
            <strong>Description:</strong>
            <span>{competition.description}</span>
          </p>
          <p className="competition-meta-row competition-meta-row--wide">
            <strong>Submission:</strong>
            <span>{competition.submission}</span>
          </p>
          <p className="competition-meta-row">
            <strong>Sponsor:</strong>
            <span>{competition.sponsored_by}</span>
          </p>
          <p className="competition-meta-row">
            <strong>Status:</strong>
            <span className={`dashboard__competition-status dashboard__competition-status--${statusClass}`}>
              {competition.status}
            </span>
          </p>
          <p className="competition-meta-row">
            <strong>Tags:</strong>
            <span className="competition-meta-tags">
              <CompetitionTypePill type={competition.type} />
              <CompetitionModePill mode={competition.competition_mode} />
            </span>
          </p>
          {prizeTagLabel && (
            <p className="competition-meta-row">
              <strong>Prize:</strong>
              <span className="competition-meta-prize-tag">{prizeTagLabel.replace(/^Prize:\s*/, '')}</span>
            </p>
          )}
          <p className="competition-meta-row">
            <strong>Visibility:</strong>
            <CompetitionVisibilityPill visibility={competition.visibility} showLabel />
          </p>
          <p className="competition-meta-row">
            <strong>Deadline:</strong>
            <span className="competition-meta-date">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              {formatDate(competition.end_date)}
            </span>
          </p>
          {competition.participants !== undefined && (
            <p className="competition-meta-row">
              <strong>{isTeamMode ? 'Teams:' : 'Participants:'}</strong>
              <span className="competition-meta-date">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                {competition.participants} {participantUnit}
              </span>
            </p>
          )}
        </section>

        <Timeline startDate={competition.start_date} endDate={competition.end_date} />

        <div className="competition-days-remaining">
          {(() => {
            const end = new Date(competition.end_date);
            const now = new Date();
            const diffDays = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
            if (diffDays > 0) return <p>{diffDays} day{diffDays !== 1 ? 's' : ''} until competition ends</p>;
            if (diffDays === 0) return <p>Competition ends today</p>;
            return <p>Competition ended {Math.abs(diffDays)} day{Math.abs(diffDays) !== 1 ? 's' : ''} ago</p>;
          })()}
        </div>

        <section className="submission-section-container" data-tour="public-instructions">
          {competition.instructions_path ? (
            <div className="markdown-content">
              {markdownLoading ? (
                <LoadingSpinner size="sm" text="Loading instructions..." />
              ) : markdownError ? (
                <div className="markdown-error">
                  <p>{markdownError}</p>
                  <p>
                    <a href={competition.instructions_path} target="_blank" rel="noopener noreferrer">
                      Click here to view instructions in a new tab
                    </a>
                  </p>
                </div>
              ) : (
                <>
                  <div className={`markdown-container ${isMarkdownExpanded ? 'expanded' : ''}`}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdownContent}</ReactMarkdown>
                  </div>
                  <button
                    className="btn-expand-markdown"
                    onClick={() => setIsMarkdownExpanded(!isMarkdownExpanded)}
                  >
                    {isMarkdownExpanded ? '▲ Collapse' : '▼ Expand'}
                  </button>
                </>
              )}
            </div>
          ) : (
            <p>No instructions.</p>
          )}
        </section>

        {competition.leaderboard === 'public' || competition.leaderboard === 'final' ? (
          <div data-tour="public-leaderboard">
            <Leaderboard
              competitionId={competition.id}
              publicMode
              leaderboardMode={competition.leaderboard}
              competitionMode={competition.competition_mode}
            />
          </div>
        ) : competition.leaderboard === 'unlisted' ? null : (
          <div className="competition-leaderboard-locked" data-tour="public-leaderboard">
            <p>🔒 Leaderboard will be public once the competition ends</p>
          </div>
        )}
      </div>

      <SiteFooter />
    </div>
  );
};

export default PublicCompetition;
